import { useState, useEffect, useCallback } from "react";

import { Box, Typography, CircularProgress } from "@mui/material";

import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";

const EVENT_STYLES = {
  criacao: {
    icon: DescriptionIcon,
    color: themeColorsDesingSystem.primary.main,
    bg: themeColorsDesingSystem.secondary.active,
  },
  publicacao: {
    icon: CheckCircleIcon,
    color: themeColorsDesingSystem.green.main,
    bg: themeColorsDesingSystem.green.background,
  },
  edicao: {
    icon: EditIcon,
    color: "#E65100",
    bg: "#FFF3E0",
  },
  representantes: {
    icon: EditIcon,
    color: "#E65100",
    bg: "#FFF3E0",
  },
  status: {
    icon: WarningAmberIcon,
    color: themeColorsDesingSystem.error?.main ?? "#C62828",
    bg: "#FFEBEE",
  },
};

function resolveEventStyle(tipoEvento) {
  return EVENT_STYLES[tipoEvento] ?? EVENT_STYLES.edicao;
}

function TimelineEvent({ entry }) {
  const style = resolveEventStyle(entry?.TipoEvento);
  const Icon = style.icon;
  const detalhes =
    entry?.Detalhes?.length > 0
      ? entry.Detalhes
      : entry?.Descricao
        ? [entry.Descricao]
        : [];

  return (
    <Box sx={{ display: "flex", gap: 2, position: "relative", pb: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          width: 40,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: style.bg,
            color: style.color,
            zIndex: 1,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${themeColorsDesingSystem.stone.main}`,
          borderRadius: "8px",
          bgcolor: themeColorsDesingSystem.white.main,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom: detalhes.length
              ? `1px solid ${themeColorsDesingSystem.stone.light}`
              : "none",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: themeColorsDesingSystem.primary.main,
            }}
          >
            {entry?.Titulo || entry?.Descricao || entry?.Acao || "Evento"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
              color: themeColorsDesingSystem.gray.main,
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {entry?.DataAcaoFormatLong || entry?.DataAcaoFormat}
            </Typography>
          </Box>
        </Box>

        {detalhes.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: themeColorsDesingSystem.stone.light,
            }}
          >
            {detalhes.map((line) => (
              <Typography
                key={line}
                sx={{
                  fontSize: "0.875rem",
                  color: themeColorsDesingSystem.gray.dark,
                  lineHeight: 1.55,
                  mb: line === detalhes[detalhes.length - 1] ? 0 : 0.75,
                  whiteSpace: "pre-wrap",
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 2,
            py: 1.25,
            color: themeColorsDesingSystem.gray.main,
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: "0.8rem" }}>
            Por {entry?.NomeUsuario || `Usuário #${entry?.IdUsuario}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function AuditTab({ vitrineId, active }) {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const snackbar = useSnackbar();

  const loadAudit = useCallback(async () => {
    if (!vitrineId) return;
    setLoading(true);
    try {
      const response = await Api.get(
        "virtual-showcase-b2b-historico-list",
        "showcaseB2B",
        `?idVitrineVirtualB2b=${vitrineId}`,
        true,
      );
      setEntries(response?.Result ?? []);
    } catch {
      snackbar.showMessage("Falha ao carregar histórico da vitrine.", {
        severity: "error",
      });
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [vitrineId, snackbar]);

  useEffect(() => {
    if (active && vitrineId) {
      loadAudit();
    }
  }, [active, vitrineId, loadAudit]);

  useEffect(() => {
    if (!active) {
      setEntries([]);
    }
  }, [active]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!entries.length) {
    return (
      <Typography color="textSecondary" align="center" py={4}>
        Nenhum registro de histórico encontrado para esta vitrine.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon sx={{ color: themeColorsDesingSystem.primary.main }} />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: themeColorsDesingSystem.primary.main,
            }}
          >
            Histórico de Alterações
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: themeColorsDesingSystem.gray.main,
          }}
        >
          {entries.length === 1
            ? "1 registro encontrado"
            : `${entries.length} registros encontrados`}
        </Typography>
      </Box>

      <Box sx={{ position: "relative", pl: 0.5 }}>
        <Box
          sx={{
            position: "absolute",
            left: 18,
            top: 18,
            bottom: 18,
            width: 2,
            bgcolor: themeColorsDesingSystem.stone.main,
          }}
        />
        {entries.map((entry) => (
          <TimelineEvent
            key={entry.IdVitrineVirtualB2bHistorico ?? `${entry.Acao}-${entry.DataAcao}`}
            entry={entry}
          />
        ))}
      </Box>
    </Box>
  );
}
